import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import {connect} from 'react-redux';
import ReactTooltip from 'react-tooltip';

import {
  fetchOccupancyReportForPeriod,
  selectOccupancy,
  selectOccupancyLoading,
  selectOccupancyError,
  UNITS_CHOICES,
  RESOLUTION_OPTIONS,
  VIEW_CHOICES,
  REPORT_SHAPE,
  OCCUPANCY_TYPE_CHOICES
} from 'ducks/occupancy';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {exportXlsxInBackground, selectXlsxExporting} from 'ducks/files';

import {exportOccupancyReport} from 'sdk/occupancy';

import {formatDateForApi, DATE_FORMAT} from 'utils/datetime';
import {removeRevenueSegments} from 'utils/occupancy';

import downloadIcon from 'assets/icons/download.svg';

import Notice from 'components/Notice';
import Loader from 'components/Loader';

import {OverviewSectionHeader, OverviewChart} from './components';
import './styles.css';

const percentUnitsRenderer = (currentValue, maxValue) =>
  maxValue === 0 ? '0.00%' : `${_.round(currentValue / maxValue * 100, 2)}%`;
const numberUnitsRenderer = currentValue => _.round(currentValue, 2);
const unitsRenderers = {
  [UNITS_CHOICES.PERCENT]: percentUnitsRenderer,
  [UNITS_CHOICES.NUMBERS]: numberUnitsRenderer
};

const REPORT_EXPORT_FILENAME = 'occupancy';

class OverviewSection extends React.Component<any> {
  defaultResolution = RESOLUTION_OPTIONS.MONTHLY;
  defaultShape = this.props.currentOverviewSection;
  defaultView = VIEW_CHOICES.OCCUPIED;

  state = {
    unitsRenderer: UNITS_CHOICES.PERCENT,
    openedNestedGroups: [],
    startDate: this.defaultResolution.defaultStartDate,
    endDate: this.defaultResolution.defaultEndDate,
    resolution: this.defaultResolution,
    firstPeriodChange: false,
    shouldShowOverviewChart: true,
    tooBigResolutionPeriod: false,

    view: this.defaultView,
    shape: this.defaultShape
  };

  componentDidMount() {
    this.fetchOccupancyReportForPeriod();
  }

  hasTooBigResolutionPeriod = () => {
    const {startDate, endDate, resolution} = this.state;

    const isDailyResolution = resolution === RESOLUTION_OPTIONS.DAILY;
    const monthDifference = moment(endDate, DATE_FORMAT).diff(
      moment(startDate, DATE_FORMAT),
      'months'
    );

    return isDailyResolution && monthDifference > 0;
  };

  fetchOccupancyReportForPeriod = () => {
    const {locationRef} = this.props;
    const {startDate, endDate, resolution} = this.state;

    const {loading, error} = this.props;
    const hasTooBigResolutionPeriod = this.hasTooBigResolutionPeriod();

    this.setState({
      shouldShowOverviewChart: !(loading || error || hasTooBigResolutionPeriod),
      tooBigResolutionPeriod: hasTooBigResolutionPeriod
    });

    if (hasTooBigResolutionPeriod) {
      return;
    }

    this.props.fetchOccupancyReportForPeriod({
      locationRef,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate),
      resolution: resolution && resolution.value
    });
  };

  toggleUnitsRenderer = value => {
    if (_.get(UNITS_CHOICES, value, false)) {
      throw new Error(`${value} is not a valid units choice`);
    }
    this.setState({unitsRenderer: value});
  };

  toggleViewRenderer = value => {
    if (_.get(VIEW_CHOICES, value, false)) {
      throw new Error(`${value} is not a valid view choice`);
    }
    this.setState({view: value}, () => ReactTooltip.rebuild());
  };

  toggleShapeRenderer = value => {
    if (_.get(REPORT_SHAPE, value, false)) {
      throw new Error(`${value} is not a valid occupied shape choice`);
    }
    if (value.key === 'roomPurpose') {
      this.props.toggleOverviewSectionRenderer(value);
    }
    this.setState(
      {shape: value},
      /*
      As soon as we attach dynamically new elements to the DOM,
      event handlers for react-tooltip for the new elements will not be automatically attached!!!
      So each time we add new element with a tooltip to the DOM, we need to call
      ```
      ReactTooltip.rebuild()
      ```
      => to make sure react-tooltip will appear
      */
      () => ReactTooltip.rebuild()
    );
  };

  getUnitsRenderer = () => unitsRenderers[this.state.unitsRenderer];

  openNestedGroup = nestedGroupValue => {
    const groupsWithoutValue = this.state.openedNestedGroups.filter(
      x => x !== nestedGroupValue
    );
    this.setState(
      {
        openedNestedGroups: [...groupsWithoutValue, nestedGroupValue]
      },
      /*
      As soon as we attach dynamically new elements to the DOM,
      event handlers for react-tooltip for the new elements will not be automatically attached!!!
      So each time we add new element with a tooltip to the DOM, we need to call
      ```
      ReactTooltip.rebuild()
      ```
      => to make sure react-tooltip will appear
      */
      () => ReactTooltip.rebuild()
    );
  };

  closeNestedGroup = nestedGroupValue => {
    const groupsWithoutValue = this.state.openedNestedGroups.filter(
      x => x !== nestedGroupValue
    );
    this.setState({openedNestedGroups: groupsWithoutValue});
  };

  toggleNestedGroup = nestedGroupValue => {
    if (!_.find(this.state.openedNestedGroups, x => x === nestedGroupValue)) {
      this.openNestedGroup(nestedGroupValue);
    } else {
      this.closeNestedGroup(nestedGroupValue);
    }
  };

  onResolutionChange = newResolution => {
    if (newResolution === this.state.resolution) {
      return;
    }

    // Sets the default periods only if the period hasn't been changed
    if (!this.state.firstPeriodChange) {
      this.setState(
        {
          resolution: newResolution,
          startDate: newResolution.defaultStartDate,
          endDate: newResolution.defaultEndDate
        },
        this.fetchOccupancyReportForPeriod
      );
    } else {
      this.setState(
        {resolution: newResolution},
        this.fetchOccupancyReportForPeriod
      );
    }
  };

  onPeriodChange = (field, value) =>
    this.setState(
      {[field]: value, firstPeriodChange: true},
      this.fetchOccupancyReportForPeriod
    );

  exportReport = () => {
    const {locationRef} = this.props;
    const {startDate, endDate, resolution} = this.state;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.EXPORT_OCCUPANCY_REPORT
      },
      () => {
        const exportData = {
          locationRef,
          periodStart: formatDateForApi(startDate),
          periodEnd: formatDateForApi(endDate),
          resolution: resolution && resolution.value
        };

        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportOccupancyReport,
          fileName: REPORT_EXPORT_FILENAME
        });
      }
    );
  };

  getChartData = () => {
    const {occupancy, activeTab} = this.props;
    const {view, shape} = this.state;

    if (view === VIEW_CHOICES.OCCUPIED) {
      const elementsList = occupancy[view][activeTab][shape.key].elementsList;
      const tableHeaderData =
        occupancy[view][activeTab][shape.key].tableHeaderData;

      let segments = shape.segments;

      if (shape === REPORT_SHAPE.BOOKING_TYPE) {
        segments = occupancy.occupied[activeTab].bookingType.tableHeaderData;
      } else {
        segments =
          activeTab === OCCUPANCY_TYPE_CHOICES.PHYSICAL_OCCUPANCY
            ? removeRevenueSegments(segments)
            : segments;
      }

      return {elementsList, tableHeaderData, segments};
    }

    if (view === VIEW_CHOICES.AVAILABLE) {
      const elementsList = occupancy[view].elementsList;
      const tableHeaderData = occupancy[view].tableHeaderData;
      const segments = occupancy.available.tableHeaderData;

      return {elementsList, tableHeaderData, segments};
    }

    return {};
  };

  render() {
    const {loading, exporting, error, locationRef, activeTab} = this.props;
    const {
      startDate,
      endDate,
      resolution,
      view,
      shape,
      shouldShowOverviewChart,
      tooBigResolutionPeriod
    } = this.state;
    const {elementsList, tableHeaderData, segments} = this.getChartData();

    return (
      <div className="OverviewSection colab-medium-border-radius">
        <OverviewSectionHeader
          toggleViewRenderer={this.toggleViewRenderer}
          toggleUnitsRenderer={this.toggleUnitsRenderer}
          onResolutionChange={this.onResolutionChange}
          onPeriodChange={this.onPeriodChange}
          exportReport={this.exportReport}
          error={error}
          loading={loading}
          startDate={startDate}
          endDate={endDate}
          resolution={resolution}
          exporting={exporting}
        />
        {tooBigResolutionPeriod && (
          <Notice
            noticeIcon={downloadIcon}
            noticeText={`
              Unfortunately we cannot display a date range this far into the future,
              you can however download a CSV of this data
              `}
            noticeAction={this.exportReport}
            noticeActionText="Click to download your CSV"
          />
        )}
        {loading && <Loader />}
        {shouldShowOverviewChart && (
          <OverviewChart
            elementsList={elementsList}
            tableHeaderData={tableHeaderData}
            disableShapeSwitch={view === VIEW_CHOICES.AVAILABLE}
            toggleNestedGroup={this.toggleNestedGroup}
            toggleShapeRenderer={this.toggleShapeRenderer}
            openedNestedGroups={this.state.openedNestedGroups}
            unitsRenderer={this.getUnitsRenderer()}
            resolution={resolution}
            segments={segments}
            currentShape={shape}
            locationRef={locationRef}
            occupancyChoice={activeTab}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const occupancy = selectOccupancy(state);

  ReactTooltip.rebuild();

  return {
    occupancy,
    loading: selectOccupancyLoading(state),
    error: _.get(selectOccupancyError(state), '_error'),
    exporting: selectXlsxExporting(state, REPORT_EXPORT_FILENAME)
  };
};

const mapDispatchToProps = {
  fetchOccupancyReportForPeriod,
  exportXlsxInBackground,
  requiresPermission
};

export default connect(mapStateToProps, mapDispatchToProps)(OverviewSection);
